import { apiPost, apiPostSession } from '../client';
import { LONG_TIMEOUT_MS } from '../../config/env';
import type {
  BankInstallmentsRequest,
  BankInstallmentsResponse,
  BinCodeRequest,
  BinCodeResponse,
  CompleteStripePaymentRequest,
  CompleteStripePaymentResponse,
  CreditLimitResponse,
  GetPayableBanksResponse,
  PaymentResultRequest,
  PaymentResultResponse,
} from '../types/payment';

/** BIN lookup — first 6/8 digits → bank + card metadata. */
export function getBinCode(code: string): Promise<BinCodeResponse> {
  return apiPost<BinCodeRequest, BinCodeResponse>('Payment/GetBinCode', { code });
}

/**
 * `Payment/GetPayableBanks` — enumerate the acquiring-bank ordinals with Turkish
 * display names. Body is `{ sessionHeader }` (apiPost injects it). Use this to drive
 * a bank picker instead of hardcoding the `PayableBank` enum.
 */
export function getPayableBanks(): Promise<GetPayableBanksResponse> {
  return apiPost<Record<string, never>, GetPayableBanksResponse>('Payment/GetPayableBanks', {});
}

/**
 * `POST /GetCreditLimit` — the agency's remaining current-account ("cari") credit limit.
 * Per `Docs/api/04-commerce-payments.md` the request body IS the bare SessionHeader
 * (`{ sessionId, sessionToken }`) — exactly what `apiPostSession` posts. The response is
 * returned directly (not `Ok()`-wrapped) but the client interceptor still camel-cases it.
 */
export function getCreditLimit(): Promise<CreditLimitResponse> {
  return apiPostSession<CreditLimitResponse>('GetCreditLimit');
}

/** Installment options for a card against a specific cart (Sipay token or the cart's
 *  precomputed `PaymentOption.InstallmentOptions`). */
export function getBankInstallments(body: BankInstallmentsRequest): Promise<BankInstallmentsResponse> {
  return apiPost<BankInstallmentsRequest, BankInstallmentsResponse>('Payment/GetBankInstallments', body);
}

/** Stripe payment-intent completion (legacy `PaymentController/CompleteStripePayment`). */
export function completeStripePayment(
  body: CompleteStripePaymentRequest
): Promise<CompleteStripePaymentResponse> {
  return apiPost<CompleteStripePaymentRequest, CompleteStripePaymentResponse>(
    'Payment/CompleteStripePayment',
    body,
    { timeoutMs: LONG_TIMEOUT_MS }
  );
}

/**
 * Poll-able payment result for the 3D Secure return leg. The SPA can't receive the
 * bank's server-side form POST, so after the browser lands back on `payment/return` it
 * polls this until the status settles. `PaymentReturnPage` falls back to re-fetching
 * the ShoppingCart's paid/finalized flags if this ever fails.
 */
export function getPaymentResult(shoppingCartId: string): Promise<PaymentResultResponse> {
  return apiPost<PaymentResultRequest, PaymentResultResponse>('Payment/GetPaymentResult', {
    shoppingCartId,
  });
}
